import {
  differenceInMonths,
  differenceInYears,
  parseISO,
  isWithinInterval,
  setYear,
  getYear,
} from 'date-fns';
import {
  PERSOENLICHE_FELDER,
  PASS_FELDER,
  DOKUMENT_TYPEN,
  PASSART_OPTIONS,
  getLandOptions,
} from './teilnehmer-felder';
import { Role } from '@/core/user/roles.js';

// Prüft, ob der angemeldete User die Teilnehmerdaten bearbeiten bzw Dokumente einsehen darf
// (nur Admin und DOM und CSM)
export function hasTeilnehmerdatenVollzugriff(store) {
  const roles = store.getters.customRoles ?? store.getters.parsedRoles;
  return Array.isArray(roles) && roles.some(role => [Role.ADMIN, Role.DOM, Role.CSM].includes(role));
}

// Ein Feld gilt als Pflichtfeld, sobald mindestens eines seiner Flags
// in der Releaseprozess-Konfiguration der Reise aktiv ist
export function isFeldRequired(konfiguration, feld) {
  if (konfiguration == null) return false;
  return feld.flags.some(flag => konfiguration[flag] === true);
}

export function isFeldGefuellt(teilnehmer, key) {
  const value = teilnehmer?.[key];
  return value != null && value !== '';
}

// Auswahlfelder mit Länderbezug bekommen ihre Optionen erst beim Zugriff, da die Länder
// asynchron in den Store geladen werden
const LAND_FELD_KEYS = ['geburtsland', 'passLand'];

function mitLandOptions(feld) {
  return LAND_FELD_KEYS.includes(feld.key) ? { ...feld, options: getLandOptions() } : feld;
}

export function getRequiredPersoenlicheFelder(konfiguration) {
  return PERSOENLICHE_FELDER.filter(feld => isFeldRequired(konfiguration, feld)).map(mitLandOptions);
}

export function getRequiredPassFelder(konfiguration) {
  return PASS_FELDER.filter(feld => isFeldRequired(konfiguration, feld));
}

// Auswahloptionen des Passart-Feldes sind die validePassarten der Konfiguration
// ohne Konfigurationswerte stehen alle Passarten zur Auswahl
export function getPassartOptions(konfiguration) {
  const validePassarten = konfiguration?.validePassarten ?? [];
  if (validePassarten.length === 0) return PASSART_OPTIONS;
  return validePassarten
    .map(passart => PASSART_OPTIONS.find(option => option.value === passart))
    .filter(option => option != null);
}

// Anzeige-Felder der Passdaten-Spalte: die Pflichtfelder plus die Felder aus Blank
// und Kundenformular (Passname, Passart), die nicht über Required-Flags konfiguriert werden
export function getSichtbarePassFelder(konfiguration) {
  const pflichtFelder = getRequiredPassFelder(konfiguration);
  if (pflichtFelder.length === 0) return [];
  const passartSichtbar = (konfiguration?.validePassarten?.length ?? 0) > 0;
  return PASS_FELDER.filter(
    feld =>
      pflichtFelder.includes(feld) || feld.key === 'passName' || (feld.key === 'passArt' && passartSichtbar)
  )
    .map(feld => (feld.key === 'passArt' ? { ...feld, options: getPassartOptions(konfiguration) } : feld))
    .map(mitLandOptions);
}

export function getRequiredDokumentTypen(konfiguration) {
  return DOKUMENT_TYPEN.filter(typ => isFeldRequired(konfiguration, typ));
}

export function getSichtbareDokumentTypen(konfiguration) {
  const pflichtTypen = getRequiredDokumentTypen(konfiguration);
  return DOKUMENT_TYPEN.filter(typ => pflichtTypen.includes(typ) || typ.optional === true);
}

// Ermittelt für die Fortschrittsanzeige, wie viele der laut Konfiguration
// erforderlichen Felder (persönliche Daten und Passdaten) bereits ausgefüllt sind
export function getPassdatenProgress(teilnehmer, konfiguration) {
  const requiredFelder = [
    ...getRequiredPersoenlicheFelder(konfiguration),
    ...getRequiredPassFelder(konfiguration),
  ];
  const filled = requiredFelder.filter(feld => isFeldGefuellt(teilnehmer, feld.key)).length;
  return { filled, total: requiredFelder.length };
}

// Zählt, wie viele der laut Konfiguration erforderlichen Dokumenttypen bereits hochgeladen sind
export function getDokumenteProgress(dokumente, konfiguration) {
  const requiredTypen = getRequiredDokumentTypen(konfiguration);
  const vorhandeneTypen = new Set((dokumente || []).map(dokument => dokument.dokumentTyp));
  const filled = requiredTypen.filter(typ => vorhandeneTypen.has(typ.key)).length;
  return { filled, total: requiredTypen.length };
}

// Die Nationalität liegt je nach Datenquelle in unterschiedlichen Schreibweisen vor
// (Ländercode oder ausgeschrieben), daher werden mehrere Varianten geprüft
export function isNationalitaetDeutsch(nationalitaet) {
  if (!nationalitaet) return false;
  return ['deutsch', 'german', 'de', 'deutschland'].includes(nationalitaet.toLowerCase());
}

// Leitet die Anrede aus dem Geschlecht ab, bei Divers/Unbestimmt und fehlendem Wert bleibt sie leer
export function getAnredeAusGeschlecht(geschlecht) {
  if (geschlecht === 'M') return 'Herr';
  if (geschlecht === 'F') return 'Frau';
  return '';
}

export function berechneAlter(geburtsdatum) {
  if (!geburtsdatum) return null;
  return differenceInYears(new Date(), parseISO(geburtsdatum));
}

// Liefert den Reisezeitraum des Reisetermins als { start, end } für die Teilnehmer-Prüfungen
// (Geburtstags-Hinweis, Passgültigkeit)

export function getReiseterminZeitraum(reisetermin) {
  return {
    start: reisetermin?.abreisedatum ?? null,
    end: reisetermin?.enddatum ?? null,
  };
}

// Prüft, ob der Geburtstag in den Reisezeitraum fällt
export function isBirthdayDuringTravelPeriod(geburtsdatum, reiseterminStartdatum, reiseterminEnddatum) {
  if (!geburtsdatum || !reiseterminStartdatum || !reiseterminEnddatum) return false;
  const start = parseISO(reiseterminStartdatum);
  const end = parseISO(reiseterminEnddatum);
  const parsedBirth = parseISO(geburtsdatum);
  const birthInStartYear = setYear(parsedBirth, getYear(start));
  const birthInEndYear = setYear(parsedBirth, getYear(end));
  return (
    isWithinInterval(birthInStartYear, { start, end }) || isWithinInterval(birthInEndYear, { start, end })
  );
}

// Prüft, ob der Pass bei Reisebeginn weniger als sieben Monate gültig ist
export function isPassLessThanSevenMonthsValid(passGueltigkeitsdatum, reiseterminStartdatum) {
  if (!passGueltigkeitsdatum || !reiseterminStartdatum) return false;
  return differenceInMonths(parseISO(passGueltigkeitsdatum), parseISO(reiseterminStartdatum)) < 7;
}
